/**
 * @file partial-deliver-span.tsx
 * @module app/modules/_common/components/partial-deliver-span
 * @description Reusable component to display partial delivery status icon with tooltip.
 * Renders a partial delivery indicator when order is partially delivered
 * (total quantities > processed quantities && processed quantities > 0).
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.0
 * @version 1.3.0
 */

import { OverlayTrigger, Popover } from 'react-bootstrap';
import { IMAGES } from '../../../../utils/assetsAndPermissions';

interface PartialDeliverSpanProps {
  orderVal: any;
  className?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

export const PartialDeliverSpan: React.FC<PartialDeliverSpanProps> = ({
  orderVal,
  className = 'me-2',
  placement = 'top',
}) => {
  // Check if order is partially delivered:
  // - Total quantities > processed quantities (not all items delivered)
  // - Processed quantities > 0 (at least some items were delivered)
  const isPartialDelivery =
    orderVal?.totalQuantities?.reduce(
      (total: number, item: any) => total + item.stockCount,
      0
    ) > orderVal?.mProcessedQuantities && orderVal?.mProcessedQuantities !== 0;

  if (!isPartialDelivery) {
    return null;
  }

  return (
    <OverlayTrigger
      trigger={['hover', 'focus']}
      placement={placement}
      overlay={
        <Popover className="bg-dark">
          <Popover.Body className="text-white fw-500 py-2 px-3">
            Partial Delivery
          </Popover.Body>
        </Popover>
      }
    >
      <img
        className={`${className} release-highlighter--partial-delivery`}
        width={28}
        height={21}
        src={IMAGES.PartialTruckTwo}
        alt="Partial Delivery"
        style={{ cursor: 'pointer' }}
      />
    </OverlayTrigger>
  );
};

export default PartialDeliverSpan;
